import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  FiscalBlockPanel,
  PrintedReceiptView,
  RegisterTextView,
  printedQrPayload,
} from '@/components/receipts/receipt-fiscal-view';
import { useHasPermission } from '@/auth/auth-store';
import {
  FISCAL_DOCUMENT_LABEL,
  FISCAL_DOCUMENT_LABEL_KEY,
  JOURNAL_TYPE_LABEL,
  JOURNAL_TYPE_LABEL_KEY,
  downloadJournalCsv,
  useJournalDocument,
  useJournalDocuments,
  useJournalFiscalDocument,
  useJournalRegisters,
  useJournalSummary,
  type JournalBackOfficeRecord,
  type JournalDocument,
  type JournalEntry,
  type JournalFiscalDocument,
  type JournalFiscalDocumentDetail,
  type JournalZTotals,
  type JournalGap,
  type JournalMoneyTotals,
  type JournalRegister,
  type JournalScope,
  type JournalShift,
} from '@/hooks/use-fiscal-journal';
import { formatMinor } from '@/hooks/use-pricing';
import {
  CASH_MOVEMENT_KIND_LABEL,
  CASH_MOVEMENT_KIND_LABEL_KEY,
  type CashMovementKind,
} from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';
import { readFiscalBlock, readPrintedReceipt, type FiscalBlockView } from '@/lib/receipt-document';
import { cn } from '@/lib/utils';
import { EmptyPanel, FiscalCabinetSection } from './fiscal-cabinet-section';

/** The server's bound; the date pickers count calendar days, both ends included. */
const MAX_RANGE_DAYS = 31;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DD` of a date in the browser's time zone. */
export function localDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight at the start of `day`. */
function dayStart(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

function nextDay(day: string): Date {
  const start = dayStart(day);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
}

/** Calendar days from `from` to `to`, both included (0 when `to` is before `from`). */
function daysBetween(from: string, to: string): number {
  const a = dayStart(from);
  const b = dayStart(to);
  if (b < a) return 0;
  return (
    Math.round(
      (Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) -
        Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) /
        86_400_000,
    ) + 1
  );
}

/**
 * PFC-23: the register, the date range and the shift live in the URL, so a
 * refresh, browser Back / Forward and a shared link land on the same journal.
 * The keys are short and stable because operators copy these links.
 * PFC-25 adds the store filter.
 */
export const JOURNAL_URL_KEYS = {
  store: 'store',
  terminal: 'terminal',
  from: 'from',
  to: 'to',
  shift: 'shift',
} as const;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** Shift ids are generated ids; anything else in the URL is not one. */
const SHIFT_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** A real calendar day `YYYY-MM-DD` (not `2026-02-31`, not a year before 100). */
export function isCalendarDay(raw: string): boolean {
  return ISO_DAY.test(raw) && localDay(dayStart(raw)) === raw;
}

export type JournalUrlSelection = {
  /** The store the till picker is narrowed to; null for all stores. */
  storeId: string | null;
  /** The register the URL names; null when it names none or one not offered. */
  terminalId: string | null;
  fromDay: string;
  toDay: string;
  shiftId: string | undefined;
  /** URL keys whose values this page cannot use; they are removed from the URL. */
  invalid: string[];
};

/** PFC-25: a revoked till is not offered in the picker, only reachable by link. */
function isPickable(r: JournalRegister): boolean {
  return r.terminalStatus !== 'revoked';
}

/** The stores of the pickable tills, by name: the store filter's choices. */
export function journalStores(
  registers: readonly JournalRegister[],
): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  for (const r of registers) {
    if (r.location !== null && isPickable(r)) byId.set(r.location.id, r.location.name);
  }
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/** The tills the picker offers for a store (all stores when null), revoked ones left out. */
export function journalPickableRegisters(
  registers: readonly JournalRegister[],
  storeId: string | null,
): JournalRegister[] {
  return registers.filter((r) => isPickable(r) && (storeId === null || r.location?.id === storeId));
}

/**
 * The journal selection a URL carries, validated: a malformed day falls back
 * to `today`, a store with no pickable till is dropped, and a register that
 * is not in `registers` (the ones the API offers) or not in the chosen store
 * falls back to the default one and takes its shift with it. A revoked
 * register stays valid: old links and history must still open (PFC-25).
 * While the registers are still loading (`registers` undefined) the store and
 * the register are taken as given and judged once they arrive.
 */
export function journalSelectionFromUrl(
  params: URLSearchParams,
  today: string,
  registers: readonly JournalRegister[] | undefined,
): JournalUrlSelection {
  const invalid: string[] = [];
  const day = (key: string): string => {
    const raw = params.get(key);
    if (raw === null) return today;
    if (isCalendarDay(raw)) return raw;
    invalid.push(key);
    return today;
  };
  const fromDay = day(JOURNAL_URL_KEYS.from);
  const toDay = day(JOURNAL_URL_KEYS.to);

  let storeId = params.get(JOURNAL_URL_KEYS.store);
  if (
    storeId !== null &&
    registers !== undefined &&
    !journalStores(registers).some((s) => s.id === storeId)
  ) {
    invalid.push(JOURNAL_URL_KEYS.store);
    storeId = null;
  }

  let terminalId = params.get(JOURNAL_URL_KEYS.terminal);
  let terminalUnknown = false;
  if (terminalId !== null && registers !== undefined) {
    const named = registers.find((r) => r.terminalId === terminalId);
    if (named === undefined || (storeId !== null && named.location?.id !== storeId)) {
      invalid.push(JOURNAL_URL_KEYS.terminal);
      terminalId = null;
      terminalUnknown = true;
    }
  }

  const rawShift = params.get(JOURNAL_URL_KEYS.shift);
  let shiftId: string | undefined;
  if (rawShift !== null) {
    // A shift belongs to one register: it cannot outlive an unknown one.
    if (terminalUnknown || !SHIFT_ID.test(rawShift)) invalid.push(JOURNAL_URL_KEYS.shift);
    else shiftId = rawShift;
  }
  return { storeId, terminalId, fromDay, toDay, shiftId, invalid };
}

/** The URL of a complete selection, so a copied link names everything it shows. */
function journalSearch(sel: {
  storeId: string | null;
  terminalId: string;
  fromDay: string;
  toDay: string;
  shiftId: string | undefined;
}): URLSearchParams {
  const next = new URLSearchParams();
  if (sel.storeId !== null) next.set(JOURNAL_URL_KEYS.store, sel.storeId);
  next.set(JOURNAL_URL_KEYS.terminal, sel.terminalId);
  next.set(JOURNAL_URL_KEYS.from, sel.fromDay);
  next.set(JOURNAL_URL_KEYS.to, sel.toDay);
  if (sel.shiftId !== undefined) next.set(JOURNAL_URL_KEYS.shift, sel.shiftId);
  return next;
}

function isNotFound(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 404;
}

function when(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleString();
}

function Money({ minor, currency }: { minor: string | null; currency: string | null }) {
  if (minor === null) return <>—</>;
  return (
    <>
      {formatMinor(minor)}
      {currency !== null ? ` ${currency}` : ''}
    </>
  );
}

function registerLabel(r: JournalRegister, t: (key: string, fallback: string) => string): string {
  const store = r.location?.name ?? '—';
  if (r.prro !== null && r.prro.fiscalNumber !== null) {
    return t(
      'fiscalCabinet.journal.registerOption',
      'FN {fiscalNumber} · local no. {localNumber} · {prroName} · {store}',
    )
      .replace('{fiscalNumber}', r.prro.fiscalNumber)
      .replace('{localNumber}', r.prro.localNumber)
      .replace('{prroName}', r.prro.prroName)
      .replace('{store}', store);
  }
  return t(
    'fiscalCabinet.journal.registerOptionNoPrro',
    '{terminal} ({code}) · {store} · no PRRO data',
  )
    .replace('{terminal}', r.terminalName)
    .replace('{code}', r.terminalCode)
    .replace('{store}', store);
}

function paymentText(doc: JournalDocument, discountWord: string): string {
  if (doc.payments === null || doc.payments.length === 0) return doc.tenderKind ?? '—';
  return doc.payments
    .map(
      (p) =>
        `${p.method} ${formatMinor(p.amountMinor)}${p.discountClassified ? ` (${discountWord})` : ''}`,
    )
    .join(' · ');
}

function PaymentBreakdown({ totals }: { totals: JournalMoneyTotals }) {
  const t = useT('admin_ui');
  if (totals.byPayment.length === 0) return <>—</>;
  return (
    <ul className="space-y-0.5">
      {totals.byPayment.map((p) => (
        <li key={p.method ?? '__none'} className="whitespace-nowrap">
          <span className="text-muted-foreground">
            {p.method ?? t('fiscalCabinet.journal.paymentNotRecorded', 'not recorded')}
          </span>{' '}
          {formatMinor(p.netMinor)}
        </li>
      ))}
    </ul>
  );
}

function GapText({ gap }: { gap: JournalGap }) {
  const t = useT('admin_ui');
  const range =
    gap.missingFrom === gap.missingTo ? gap.missingFrom : `${gap.missingFrom}–${gap.missingTo}`;
  return (
    <span>
      <span className="font-mono">{range}</span>{' '}
      <span className="text-muted-foreground">
        {(gap.nearOfflineSession
          ? t('fiscalCabinet.journal.gapOfflineCount', '({count} offline session documents)')
          : t('fiscalCabinet.journal.gapCount', '({count} missing)')
        ).replace('{count}', gap.missingCount)}
        {gap.betweenShifts &&
          ` · ${t('fiscalCabinet.journal.gapBetweenShifts', 'across a shift change')}`}
      </span>
    </span>
  );
}

type Translate = (key: string, fallback: string) => string;

/** One labelled amount of a drawer line (`—` when the till recorded none). */
function DrawerPart({
  label,
  minor,
  currency,
}: {
  label: string;
  minor: string | null;
  currency: string | null;
}) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground">{label}</span>{' '}
      <span className="font-mono">
        <Money minor={minor} currency={currency} />
      </span>
    </span>
  );
}

/**
 * The drawer of a shift (or of the scope): Back Office's own records of cash
 * in / cash out, the opening float and what the till recorded at close.
 */
function DrawerLine({
  cash,
  currency,
  t,
  note = false,
  uploading = false,
}: {
  cash: JournalShift['cash'] | JournalSummaryCash;
  currency: string | null;
  t: Translate;
  /** Say once, on the last drawer line, where these figures come from. */
  note?: boolean;
  /** The till uploads the register's journal: the figures are simply Back Office's. */
  uploading?: boolean;
}) {
  const shiftCash = 'openingCashMinor' in cash ? cash : null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
      data-testid="journal-drawer"
    >
      <span className="font-medium">{t('fiscalCabinet.journal.cash.drawer', 'Cash drawer')}</span>
      {shiftCash !== null && (
        <DrawerPart
          label={t('fiscalCabinet.journal.record.openingCash', 'Opening float')}
          minor={shiftCash.openingCashMinor}
          currency={currency}
        />
      )}
      <DrawerPart
        label={t('fiscalCabinet.journal.record.cashIn', 'Cash in')}
        minor={cash.cashInMinor}
        currency={currency}
      />
      <DrawerPart
        label={t('fiscalCabinet.journal.record.cashOut', 'Cash out')}
        minor={cash.cashOutMinor}
        currency={currency}
      />
      {shiftCash !== null && (
        <>
          <DrawerPart
            label={t('fiscalCabinet.journal.cash.expected', 'Expected cash in drawer')}
            minor={shiftCash.closingCashExpectedMinor}
            currency={currency}
          />
          <DrawerPart
            label={t('fiscalCabinet.journal.cash.counted', 'Counted')}
            minor={shiftCash.closingCashCountedMinor}
            currency={currency}
          />
          <DrawerPart
            label={t('fiscalCabinet.journal.cash.variance', 'Variance')}
            minor={shiftCash.varianceMinor}
            currency={currency}
          />
        </>
      )}
      {note && (
        <span className="text-muted-foreground">
          (
          {uploading
            ? t('fiscalCabinet.journal.cash.fromBackOffice', 'Back Office records')
            : t(
                'fiscalCabinet.journal.record.backOffice',
                'Back Office record — fiscal text not uploaded (RL-540)',
              )}
          )
        </span>
      )}
    </div>
  );
}

type JournalSummaryCash = { cashInMinor: string; cashOutMinor: string; movementCount: number };

/**
 * A shift's numbering gaps: the missing numbers, and apart from them the ones
 * next to an offline session (the session's own documents, PFC-24).
 */
function ShiftGaps({
  gaps,
  truncated,
  t,
}: {
  gaps: JournalGap[];
  truncated: boolean;
  t: Translate;
}) {
  if (gaps.length === 0) return <>—</>;
  const missing = gaps.filter((g) => !g.nearOfflineSession);
  const offline = gaps.filter((g) => g.nearOfflineSession);
  const sum = (list: JournalGap[]) => list.reduce((n, g) => n + Number(g.missingCount), 0);
  // Only the first listed gaps reach a shift; more may follow.
  const more = truncated ? '+' : '';
  return (
    <span className="flex flex-wrap gap-1">
      {missing.length > 0 && (
        <Badge variant="destructive">
          {sum(missing)}
          {more}
        </Badge>
      )}
      {offline.length > 0 && (
        <Badge
          variant="outline"
          title={t('fiscalCabinet.journal.offlineDocuments', 'offline session documents')}
        >
          {t('fiscalCabinet.journal.offlineShort', 'offline')} {sum(offline)}
          {more}
        </Badge>
      )}
    </span>
  );
}

function ShiftTotalsTable({
  shifts,
  totals,
  cash,
  selectedShiftId,
  onSelect,
  gapsTruncated,
  uploading,
  onOpen,
}: {
  shifts: JournalShift[];
  totals: JournalMoneyTotals;
  cash: JournalSummaryCash;
  gapsTruncated: boolean;
  selectedShiftId: string | undefined;
  onSelect: (shiftId: string | undefined) => void;
  uploading: boolean;
  /** Open a journal entry (the register's Z of a shift). */
  onOpen: (id: string) => void;
}) {
  const t = useT('admin_ui');
  const moneyCell = (totalsOf: JournalMoneyTotals, minor: string | null) =>
    totalsOf.mixedCurrency ? (
      <span className="text-xs text-muted-foreground">
        {t('fiscalCabinet.journal.mixedCurrency', 'mixed or unknown currencies, not summed')}
      </span>
    ) : (
      <Money minor={minor} currency={totalsOf.currency} />
    );
  return (
    <div className="rounded-md border border-border bg-card">
      <header className="flex items-center justify-between border-b border-border px-4 py-2 text-sm font-medium">
        <span>{t('fiscalCabinet.journal.shiftTotals', 'Shift totals')}</span>
        {selectedShiftId !== undefined && (
          <Button variant="outline" size="sm" onClick={() => onSelect(undefined)}>
            {t('fiscalCabinet.journal.allShifts', 'All shifts')}
          </Button>
        )}
      </header>
      <Table>
        <THead>
          <TR>
            <TH>{t('fiscalCabinet.journal.col.shift', 'Shift')}</TH>
            <TH>{t('receipts.field.cashier', 'Cashier')}</TH>
            <TH>{t('fiscalCabinet.journal.col.numbers', 'Numbers')}</TH>
            <TH className="text-right">{t('fiscalCabinet.journal.col.documents', 'Documents')}</TH>
            <TH className="text-right">{t('fiscalCabinet.journal.col.sales', 'Sales')}</TH>
            <TH className="text-right">{t('fiscalCabinet.journal.col.refunds', 'Refunds')}</TH>
            <TH className="text-right">{t('fiscalCabinet.journal.col.net', 'Net')}</TH>
            <TH>{t('fiscalCabinet.journal.col.byPayment', 'Net by payment')}</TH>
            <TH>{t('fiscalCabinet.journal.col.gaps', 'Missing numbers')}</TH>
          </TR>
        </THead>
        <TBody>
          {shifts.map((s) => (
            <Fragment key={s.shiftId ?? '__none'}>
              <TR
                className={cn(
                  s.shiftId !== null && 'cursor-pointer hover:bg-muted/50',
                  s.shiftId !== null && s.shiftId === selectedShiftId && 'bg-muted',
                )}
                onClick={() => {
                  if (s.shiftId !== null) onSelect(s.shiftId);
                }}
              >
                <TD className="whitespace-nowrap text-xs">
                  {s.shiftId === null ? (
                    t('fiscalCabinet.journal.noShift', 'Without a shift')
                  ) : (
                    <>
                      {when(s.openedAt)} –{' '}
                      {s.closedAt === null
                        ? t('fiscalCabinet.journal.shiftOpen', 'open')
                        : when(s.closedAt)}
                      {s.zReportRef !== null && (
                        <div className="font-mono text-muted-foreground">Z {s.zReportRef}</div>
                      )}
                      {s.zDocument !== null && (
                        <button
                          type="button"
                          className="block underline"
                          onClick={(e) => {
                            // Opens the Z, without narrowing to the shift.
                            e.stopPropagation();
                            onOpen(s.zDocument!.id);
                          }}
                        >
                          {t('fiscalCabinet.journal.openZ', 'Register Z report')}
                        </button>
                      )}
                    </>
                  )}
                </TD>
                <TD>{s.cashier?.displayName ?? '—'}</TD>
                <TD className="font-mono text-xs">
                  {s.firstNumber === null ? '—' : `${s.firstNumber}–${s.lastNumber ?? ''}`}
                </TD>
                <TD className="text-right">{s.totals.documentCount}</TD>
                <TD className="text-right font-mono text-xs">
                  {moneyCell(s.totals, s.totals.salesMinor)}
                </TD>
                <TD className="text-right font-mono text-xs">
                  {moneyCell(s.totals, s.totals.refundsMinor)}
                </TD>
                <TD className="text-right font-mono text-xs">
                  {moneyCell(s.totals, s.totals.netMinor)}
                </TD>
                <TD className="font-mono text-xs">
                  <PaymentBreakdown totals={s.totals} />
                </TD>
                <TD className="text-xs">
                  <ShiftGaps gaps={s.gaps} truncated={gapsTruncated} t={t} />
                </TD>
              </TR>
              {s.shiftId !== null && (
                <TR
                  className={cn(
                    'cursor-pointer hover:bg-muted/50',
                    s.shiftId === selectedShiftId && 'bg-muted',
                  )}
                  onClick={() => {
                    if (s.shiftId !== null) onSelect(s.shiftId);
                  }}
                >
                  <TD colSpan={9} className="pt-0">
                    <DrawerLine
                      cash={s.cash}
                      currency={s.totals.mixedCurrency ? null : s.totals.currency}
                      t={t}
                    />
                  </TD>
                </TR>
              )}
            </Fragment>
          ))}
          <TR className="font-medium">
            <TD colSpan={3}>
              {selectedShiftId === undefined
                ? t('fiscalCabinet.journal.rangeTotal', 'Total for the period')
                : t('fiscalCabinet.journal.shiftTotal', 'Total for the shift')}
            </TD>
            <TD className="text-right">{totals.documentCount}</TD>
            <TD className="text-right font-mono text-xs">{moneyCell(totals, totals.salesMinor)}</TD>
            <TD className="text-right font-mono text-xs">
              {moneyCell(totals, totals.refundsMinor)}
            </TD>
            <TD className="text-right font-mono text-xs">{moneyCell(totals, totals.netMinor)}</TD>
            <TD className="font-mono text-xs">
              <PaymentBreakdown totals={totals} />
            </TD>
            <TD />
          </TR>
          <TR>
            <TD colSpan={9} className="pt-0">
              <DrawerLine
                cash={cash}
                currency={totals.mixedCurrency ? null : totals.currency}
                t={t}
                note
                uploading={uploading}
              />
            </TD>
          </TR>
        </TBody>
      </Table>
    </div>
  );
}

function DocumentPanel({ id }: { id: string }) {
  const t = useT('admin_ui');
  const { data, isLoading, isError } = useJournalDocument(id);
  const canOpenReceipt = useHasPermission('receipts.read');
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }
  const block = readFiscalBlock(data.fiscal, data.receiptDocument);
  const printed = readPrintedReceipt(data.receiptDocument);
  return (
    <div className="space-y-3" data-testid="journal-document">
      {block !== null && (
        <FiscalBlockPanel
          block={block}
          showQrImage={printed === null || printedQrPayload(printed, block.qrPayload) === null}
        />
      )}
      {printed !== null && (
        <PrintedReceiptView receipt={printed} qrPayload={block?.qrPayload ?? null} />
      )}
      {block === null && printed === null && (
        <p className="text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.journal.documentEmpty',
            'The till stored neither a fiscal block nor a printed receipt for this document.',
          )}
        </p>
      )}
      {canOpenReceipt && (
        <Link to={`/receipts/${data.id}`} className="text-sm underline">
          {t('fiscalCabinet.journal.openReceipt', 'Open in Receipts')}
        </Link>
      )}
    </div>
  );
}

function NumberingPanel({
  summary,
  onOpen,
}: {
  summary: NonNullable<ReturnType<typeof useJournalSummary>['data']>;
  onOpen: (id: string) => void;
}) {
  const t = useT('admin_ui');
  const n = summary.numbering;
  const clean =
    n.gapCount === 0 &&
    n.duplicates.length === 0 &&
    n.unnumberedCount === 0 &&
    summary.withoutFiscalBlock.count === 0;
  return (
    <section
      className="space-y-2 rounded-md border border-border bg-card p-4 text-sm"
      data-testid="journal-numbering"
    >
      <h2 className="font-medium">{t('fiscalCabinet.journal.numbering', 'Numbering check')}</h2>
      <p className="text-muted-foreground">
        {n.firstNumber === null
          ? t('fiscalCabinet.journal.noNumbers', 'No document here carries a receipt number.')
          : t(
              'fiscalCabinet.journal.numberRange',
              'Receipt numbers {first}–{last}, {count} numbered documents.',
            )
              .replace('{first}', n.firstNumber)
              .replace('{last}', n.lastNumber ?? '')
              .replace('{count}', String(n.numberedCount))}
      </p>
      {clean && (
        <p>
          <Badge variant="success">{t('fiscalCabinet.journal.noGaps', 'No missing numbers')}</Badge>
        </p>
      )}
      {n.gaps.length > 0 && (
        <div>
          {n.gapCount > 0 && (
            <p className="font-medium text-destructive">
              {t('fiscalCabinet.journal.gapsFound', '{missing} numbers missing in {runs} places:')
                .replace('{missing}', n.missingCount)
                .replace('{runs}', String(n.gapCount))}
            </p>
          )}
          {n.offlineGapCount > 0 && (
            <p className="font-medium" data-testid="journal-offline-gaps">
              {t(
                'fiscalCabinet.journal.offlineGapsFound',
                '{missing} numbers in {runs} places are offline session documents:',
              )
                .replace('{missing}', n.offlineMissingCount)
                .replace('{runs}', String(n.offlineGapCount))}
            </p>
          )}
          <ul className="ml-4 list-disc">
            {n.gaps.map((g) => (
              <li key={`${g.after}-${g.before}`}>
                <GapText gap={g} />
              </li>
            ))}
          </ul>
          {n.gapsTruncated && (
            <p className="text-xs text-muted-foreground">
              {t(
                'fiscalCabinet.journal.gapsTruncated',
                'Only the first {count} are listed.',
              ).replace('{count}', String(n.gaps.length))}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {summary.fiscalJournal.uploading
              ? t(
                  'fiscalCabinet.journal.gapsHelpUploading',
                  'A missing number is a document Back Office has not received. Numbers next to an offline session are the register’s own session documents, which it numbers but does not report to the till.',
                )
              : t(
                  'fiscalCabinet.journal.gapsHelp',
                  'A missing number is a document Back Office has not received. Tills do not send shift openings and closings or cash in and cash out documents yet, so a missing number can also be one of those, most often across a shift change.',
                )}
          </p>
        </div>
      )}
      {n.duplicates.length > 0 && (
        <p>
          {t('fiscalCabinet.journal.duplicates', 'Used by more than one document:')}{' '}
          <span className="font-mono">
            {n.duplicates.map((d) => `${d.receiptNumber} (×${d.count})`).join(', ')}
          </span>
        </p>
      )}
      {n.unnumberedCount > 0 && (
        <p>
          {t(
            'fiscalCabinet.journal.unnumbered',
            '{count} documents carry a receipt number that is not a plain number and cannot be checked.',
          ).replace('{count}', String(n.unnumberedCount))}
        </p>
      )}
      {summary.withoutFiscalBlock.count > 0 && (
        <div>
          <p className="font-medium text-destructive">
            {t(
              'fiscalCabinet.journal.withoutFiscalBlock',
              '{count} documents have no fiscal block:',
            ).replace('{count}', String(summary.withoutFiscalBlock.count))}
          </p>
          <ul className="ml-4 list-disc">
            {summary.withoutFiscalBlock.items.map((item) => (
              <li key={item.id}>
                <button type="button" className="underline" onClick={() => onOpen(item.id)}>
                  {t(
                    JOURNAL_TYPE_LABEL_KEY[item.receiptType],
                    JOURNAL_TYPE_LABEL[item.receiptType],
                  )}{' '}
                  · {when(item.finalisedAt)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function isCashMovementKind(kind: string): kind is CashMovementKind {
  return Object.prototype.hasOwnProperty.call(CASH_MOVEMENT_KIND_LABEL, kind);
}

/**
 * A Back Office record among the documents: a shift opening, a cash in /
 * cash out, or the shift close with its Z reference. Not clickable — there is
 * no stored fiscal text to open (RL-540).
 */
function BackOfficeRecordRow({
  record,
  currency,
  uploading,
}: {
  record: JournalBackOfficeRecord;
  currency: string | null;
  /** The till uploads the register's journal: no document was found for this record. */
  uploading: boolean;
}) {
  const t = useT('admin_ui');
  const withCurrency = (minor: string) =>
    `${formatMinor(minor)}${currency !== null ? ` ${currency}` : ''}`;
  let label: string;
  let amount: string | null = null;
  let details: string[] = [];
  switch (record.entry) {
    case 'shift_open':
      label = t('fiscalCabinet.journal.record.shiftOpen', 'Shift opened');
      details = [
        `${t('fiscalCabinet.journal.record.openingCash', 'Opening float')} ${withCurrency(record.openingCashMinor)}`,
      ];
      break;
    case 'cash_movement': {
      const kind = isCashMovementKind(record.kind)
        ? t(CASH_MOVEMENT_KIND_LABEL_KEY[record.kind], CASH_MOVEMENT_KIND_LABEL[record.kind])
        : record.kind;
      label = `${
        record.direction === 'in'
          ? t('fiscalCabinet.journal.record.cashIn', 'Cash in')
          : t('fiscalCabinet.journal.record.cashOut', 'Cash out')
      } · ${kind}`;
      amount =
        record.direction === 'out' ? (-BigInt(record.amountMinor)).toString() : record.amountMinor;
      details = [
        `${t('fiscalCabinet.journal.record.reason', 'Reason')}: ${record.reasonCode}`,
        ...(record.notes !== null ? [record.notes] : []),
      ];
      break;
    }
    case 'shift_close': {
      label = t('fiscalCabinet.journal.record.shiftClose', 'Z report (shift closed)');
      const money = (key: string, fallback: string, minor: string | null) =>
        minor === null ? null : `${t(key, fallback)} ${withCurrency(minor)}`;
      details = [
        record.zReportRef !== null ? `Z ${record.zReportRef}` : null,
        money(
          'fiscalCabinet.journal.cash.expected',
          'Expected cash in drawer',
          record.closingCashExpectedMinor,
        ),
        money('fiscalCabinet.journal.cash.counted', 'Counted', record.closingCashCountedMinor),
        money('fiscalCabinet.journal.cash.variance', 'Variance', record.varianceMinor),
        record.varianceReasonCode,
      ].filter((d): d is string => d !== null);
      break;
    }
  }
  return (
    <TR className="bg-muted/20 text-muted-foreground" data-testid="journal-record">
      <TD className="font-mono text-xs">—</TD>
      <TD>
        <span className="whitespace-nowrap text-foreground">{label}</span>
        <Badge variant="outline" className="ml-2 whitespace-nowrap">
          {uploading && record.entry === 'shift_open'
            ? t('fiscalCabinet.journal.cash.fromBackOffice', 'Back Office records')
            : uploading
              ? t(
                  'fiscalCabinet.journal.record.backOfficeUnpaired',
                  'Back Office record — no matching register document',
                )
              : t(
                  'fiscalCabinet.journal.record.backOffice',
                  'Back Office record — fiscal text not uploaded (RL-540)',
                )}
        </Badge>
      </TD>
      <TD className="font-mono text-xs">—</TD>
      <TD className="whitespace-nowrap text-xs">{when(record.occurredAt)}</TD>
      <TD>{record.cashier?.displayName ?? '—'}</TD>
      <TD className="text-right font-mono text-xs">
        {amount === null ? '' : <Money minor={amount} currency={currency} />}
      </TD>
      <TD className="text-xs">{details.join(' · ')}</TD>
    </TR>
  );
}

/** What a register document adds to its row: the paired Back Office record, the session. */
function fiscalDocumentDetails(
  doc: JournalFiscalDocument,
  t: Translate,
  withCurrency: (minor: string) => string,
): string[] {
  const out: string[] = [];
  if (doc.documentType === 'z_report' && doc.shiftNumber !== null) {
    out.push(`Z ${doc.shiftNumber}`);
  }
  const paired = doc.backOffice;
  if (paired?.entry === 'cash_movement') {
    const kind = isCashMovementKind(paired.kind)
      ? t(CASH_MOVEMENT_KIND_LABEL_KEY[paired.kind], CASH_MOVEMENT_KIND_LABEL[paired.kind])
      : paired.kind;
    out.push(kind, `${t('fiscalCabinet.journal.record.reason', 'Reason')}: ${paired.reasonCode}`);
    if (paired.notes !== null) out.push(paired.notes);
  } else if (paired?.entry === 'shift_close') {
    const money = (key: string, fallback: string, minor: string | null) =>
      minor === null ? null : `${t(key, fallback)} ${withCurrency(minor)}`;
    for (const part of [
      money(
        'fiscalCabinet.journal.cash.expected',
        'Expected cash in drawer',
        paired.closingCashExpectedMinor,
      ),
      money('fiscalCabinet.journal.cash.counted', 'Counted', paired.closingCashCountedMinor),
      money('fiscalCabinet.journal.cash.variance', 'Variance', paired.varianceMinor),
      paired.varianceReasonCode,
    ]) {
      if (part !== null) out.push(part);
    }
  } else if (doc.documentType === 'cash_in' || doc.documentType === 'cash_out') {
    out.push(t('fiscalCabinet.journal.doc.noMovement', 'no matching Back Office cash movement'));
  }
  if (doc.offlineSession !== null && doc.offlineSession.id !== null) {
    out.push(
      t('fiscalCabinet.journal.doc.offlineSession', 'offline session {id}').replace(
        '{id}',
        doc.offlineSession.id,
      ),
    );
  }
  if (
    (doc.documentType === 'offline_session_open' || doc.documentType === 'offline_session_close') &&
    doc.offlineSession?.documents !== null &&
    doc.offlineSession?.documents !== undefined
  ) {
    out.push(
      t('fiscalCabinet.journal.doc.offlineDocuments', '{count} documents in the session').replace(
        '{count}',
        String(doc.offlineSession.documents),
      ),
    );
  }
  return out;
}

/** Online / offline, test mode, another register: the badges a register document may carry. */
function FiscalDocumentBadges({ doc, t }: { doc: JournalFiscalDocument; t: Translate }) {
  return (
    <>
      {doc.online === false && (
        <Badge variant="outline" className="ml-2">
          {t('fiscalCabinet.journal.doc.offline', 'Offline')}
        </Badge>
      )}
      {doc.online === true && (
        <Badge variant="success" className="ml-2">
          {t('fiscalCabinet.journal.doc.online', 'Online')}
        </Badge>
      )}
      {doc.testDocument && (
        <Badge variant="destructive" className="ml-2">
          {t('fiscalCabinet.journal.doc.test', 'Test document')}
        </Badge>
      )}
      {doc.otherRegister && (
        <Badge variant="outline" className="ml-2">
          {t('fiscalCabinet.journal.doc.otherRegister', 'Register {fiscalNumber}').replace(
            '{fiscalNumber}',
            doc.registrarId ?? '',
          )}
        </Badge>
      )}
    </>
  );
}

/**
 * PFC-24 — one of the register's own documents among the receipts: an X or Z
 * report, a service cash in / out, or an offline-session marker. A cash
 * document or a Z carries the Back Office record it stands in for.
 */
function FiscalDocumentRow({
  doc,
  currency,
  open,
  onToggle,
}: {
  doc: JournalFiscalDocument;
  currency: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  const t = useT('admin_ui');
  const money = doc.currency ?? currency;
  const withCurrency = (minor: string) =>
    `${formatMinor(minor)}${money !== null ? ` ${money}` : ''}`;
  const signed =
    doc.amountMinor === null
      ? null
      : doc.documentType === 'cash_out'
        ? (-BigInt(doc.amountMinor)).toString()
        : doc.amountMinor;
  const marker =
    doc.documentType === 'offline_session_open' || doc.documentType === 'offline_session_close';
  return (
    <TR
      className={cn(
        'cursor-pointer hover:bg-muted/50',
        marker && 'bg-muted/20 text-muted-foreground',
        open && 'bg-muted',
      )}
      onClick={onToggle}
      data-testid="journal-fiscal-row"
    >
      <TD className="font-mono text-xs">{doc.receiptNumber ?? '—'}</TD>
      <TD className="whitespace-nowrap">
        <span className="text-foreground">
          {t(FISCAL_DOCUMENT_LABEL_KEY[doc.documentType], FISCAL_DOCUMENT_LABEL[doc.documentType])}
        </span>
        <FiscalDocumentBadges doc={doc} t={t} />
      </TD>
      <TD className="font-mono text-xs">{doc.fiscalNumber ?? '—'}</TD>
      <TD className="whitespace-nowrap text-xs">{when(doc.occurredAt)}</TD>
      <TD>{doc.cashier?.displayName ?? doc.cashierId ?? '—'}</TD>
      <TD className="text-right font-mono text-xs">
        {signed === null ? '' : <Money minor={signed} currency={money} />}
      </TD>
      <TD className="text-xs">{fiscalDocumentDetails(doc, t, withCurrency).join(' · ')}</TD>
    </TR>
  );
}

/** A Z report's totals as the register printed them. */
function ZTotalsTable({ totals, currency }: { totals: JournalZTotals; currency: string | null }) {
  const t = useT('admin_ui');
  const code = totals.currency ?? currency;
  const direction = (d: 'sale' | 'refund') =>
    d === 'sale'
      ? t('fiscalCabinet.journal.z.sale', 'Sales')
      : t('fiscalCabinet.journal.z.refund', 'Refunds');
  const amount = (minor: string | null) => <Money minor={minor} currency={code} />;
  return (
    <section className="rounded-md border border-border bg-card" data-testid="z-totals">
      <header className="border-b border-border px-4 py-2 text-sm font-medium">
        {t('fiscalCabinet.journal.z.title', 'Z report totals — as printed by the register')}
      </header>
      <div className="space-y-3 p-4 text-sm">
        {totals.byTaxGroup.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscalCabinet.journal.z.direction', 'Direction')}</TH>
                <TH>{t('fiscalCabinet.journal.z.taxGroup', 'Tax group')}</TH>
                <TH>{t('fiscalCabinet.journal.z.rate', 'Rate')}</TH>
                <TH>{t('fiscalCabinet.journal.z.label', 'As printed')}</TH>
                <TH className="text-right">{t('fiscalCabinet.journal.z.amount', 'Amount')}</TH>
              </TR>
            </THead>
            <TBody>
              {totals.byTaxGroup.map((g, i) => (
                <TR key={i} data-testid="z-tax-group">
                  <TD>{direction(g.direction)}</TD>
                  <TD className="font-mono">{g.taxGroup}</TD>
                  <TD className="font-mono">
                    {g.ratePercent === null ? '—' : `${g.ratePercent}%`}
                  </TD>
                  <TD className="font-mono text-xs">{g.label}</TD>
                  <TD className="text-right font-mono text-xs">{amount(g.amountMinor)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {totals.byPayment.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscalCabinet.journal.z.direction', 'Direction')}</TH>
                <TH>{t('fiscalCabinet.journal.col.payment', 'Payment')}</TH>
                <TH>{t('fiscalCabinet.journal.z.label', 'As printed')}</TH>
                <TH className="text-right">{t('fiscalCabinet.journal.z.amount', 'Amount')}</TH>
              </TR>
            </THead>
            <TBody>
              {totals.byPayment.map((p, i) => (
                <TR key={i} data-testid="z-payment">
                  <TD>{direction(p.direction)}</TD>
                  <TD>{p.method ?? '—'}</TD>
                  <TD className="font-mono text-xs">{p.label}</TD>
                  <TD className="text-right font-mono text-xs">{amount(p.amountMinor)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="z-cash">
          <DrawerPart
            label={t('fiscalCabinet.journal.z.cashBalance', 'Cash in the register')}
            minor={totals.cashBalanceMinor}
            currency={code}
          />
          <DrawerPart
            label={t('fiscalCabinet.journal.record.cashIn', 'Cash in')}
            minor={totals.cashInMinor}
            currency={code}
          />
          <DrawerPart
            label={t('fiscalCabinet.journal.record.cashOut', 'Cash out')}
            minor={totals.cashOutMinor}
            currency={code}
          />
        </div>
        {totals.lines.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-muted-foreground">
              {t('fiscalCabinet.journal.z.lines', 'Every labelled line ({count})').replace(
                '{count}',
                String(totals.lines.length),
              )}
            </summary>
            <Table>
              <TBody>
                {totals.lines.map((l, i) => (
                  <TR key={i} data-testid="z-line">
                    <TD className="text-xs text-muted-foreground">{l.section ?? ''}</TD>
                    <TD className="font-mono text-xs">{l.label}</TD>
                    <TD className="text-right font-mono text-xs">{amount(l.amountMinor)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </details>
        )}
      </div>
    </section>
  );
}

/** The fiscal block of a register document, from what the till uploaded. */
function fiscalBlockOf(doc: JournalFiscalDocumentDetail): FiscalBlockView | null {
  const stored =
    typeof doc.fiscal === 'object' && doc.fiscal !== null
      ? (doc.fiscal as Record<string, unknown>)
      : null;
  const text = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
  const block: FiscalBlockView = {
    receiptNumber: doc.receiptNumber,
    fiscalNumber: doc.fiscalNumber,
    registrarId: doc.registrarId,
    registrarLabel: null,
    timestampFormatted: doc.fiscalTimestamp,
    signature: text(stored?.signature),
    qrPayload: text(stored?.qrPayload),
    testDocument: doc.testDocument,
  };
  return Object.entries(block).some(([k, v]) => k !== 'testDocument' && v !== null) ||
    block.testDocument
    ? block
    : null;
}

/**
 * PFC-24 — one of the register's documents, opened: online or offline and
 * its session, the fiscal block, the register's printed text (its markup read
 * as on paper, the QR in its place) and, for a Z, its totals.
 */
function FiscalDocumentPanel({ documentId }: { documentId: string }) {
  const t = useT('admin_ui');
  const { data, isLoading, isError } = useJournalFiscalDocument(documentId);
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }
  const block = fiscalBlockOf(data);
  const title = t(
    FISCAL_DOCUMENT_LABEL_KEY[data.documentType],
    FISCAL_DOCUMENT_LABEL[data.documentType],
  );
  const session = data.offlineSession;
  return (
    <div className="space-y-3" data-testid="journal-fiscal-document">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-medium">{title}</span>
        <span data-testid="journal-fiscal-online">
          {data.online === true
            ? t('fiscalCabinet.journal.doc.issuedOnline', 'Issued online')
            : data.online === false
              ? t('fiscalCabinet.journal.doc.issuedOffline', 'Issued offline')
              : t('fiscalCabinet.journal.doc.onlineUnknown', 'Online or offline: not reported')}
        </span>
        {session !== null && (
          <span className="text-muted-foreground" data-testid="journal-fiscal-session">
            {t('fiscalCabinet.journal.doc.offlineSession', 'offline session {id}').replace(
              '{id}',
              session.id ?? '—',
            )}
            {session.startedAt !== null &&
              ` · ${t('fiscalCabinet.journal.doc.sessionStarted', 'started {time}').replace('{time}', session.startedAt)}`}
            {session.documents !== null &&
              ` · ${t('fiscalCabinet.journal.doc.offlineDocuments', '{count} documents in the session').replace('{count}', String(session.documents))}`}
          </span>
        )}
        {data.shiftNumber !== null && (
          <span className="text-muted-foreground">
            {t('fiscalCabinet.journal.doc.shiftNumber', 'Register shift {number}').replace(
              '{number}',
              data.shiftNumber,
            )}
          </span>
        )}
      </div>
      {block !== null && (
        <FiscalBlockPanel block={block} showQrImage={data.documentText === null} />
      )}
      {data.documentText !== null && data.documentText !== '' && (
        <RegisterTextView
          text={data.documentText}
          title={t('fiscalCabinet.journal.doc.printed', 'Printed by the register')}
          qrPayload={block?.qrPayload ?? null}
        />
      )}
      {data.zTotals !== null && <ZTotalsTable totals={data.zTotals} currency={data.currency} />}
      {data.documentText === null && data.zTotals === null && block === null && (
        <p className="text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.journal.doc.noText',
            'The register reported this without a printed document.',
          )}
        </p>
      )}
    </div>
  );
}

/** An opened journal entry: a receipt, or one of the register's own documents. */
function EntryPanel({ id }: { id: string }) {
  return id.startsWith('fj:') ? (
    <FiscalDocumentPanel documentId={id.slice('fj:'.length)} />
  ) : (
    <DocumentPanel id={id} />
  );
}

function Journal({ available }: { available: boolean }) {
  const t = useT('admin_ui');
  const registers = useJournalRegisters(available);
  const today = localDay(new Date());
  // PFC-23: the selection is the URL's; see JOURNAL_URL_KEYS.
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  // A register the URL names is judged against a settled list only: a cached
  // list being refreshed may predate the till the link names.
  const selection = journalSelectionFromUrl(
    params,
    today,
    registers.isFetching ? undefined : registers.data,
  );
  const storeId = selection.storeId;
  const shiftId = selection.shiftId;
  /**
   * A date field being edited can hold what is not a day yet (cleared, or a
   * year half typed). That stays in the field, not in the URL, and shows the
   * range error as before; it is dropped once the URL changes.
   */
  const [draft, setDraft] = useState<{ search: string; from?: string; to?: string }>({
    search,
  });
  const liveDraft = draft.search === search ? draft : { search };
  const fromDay = liveDraft.from ?? selection.fromDay;
  const toDay = liveDraft.to ?? selection.toDay;
  /**
   * The document unfolded in the list. It belongs to the selection it was
   * opened in, so Back / Forward to another register or range closes it.
   */
  const [opened, setOpened] = useState<{ search: string; id: string | null }>({
    search,
    id: null,
  });
  const openId = opened.search === search ? opened.id : null;
  const setOpenId = (id: string | null) => setOpened({ search, id });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  // PFC-25: the picker offers the chosen store's tills without the revoked
  // ones; a register the URL names is shown even when revoked. A URL store not
  // in a list still being refreshed is not judged yet, so it narrows nothing.
  const shownStore =
    storeId !== null && journalStores(registers.data ?? []).some((s) => s.id === storeId)
      ? storeId
      : null;
  // A copied link may name a store newer than the cached register list. Do
  // not query or export the first till from another store while refreshing.
  const waitingForStore = registers.isFetching && storeId !== null && shownStore === null;
  const pickable = journalPickableRegisters(registers.data ?? [], shownStore);
  const register = waitingForStore
    ? null
    : (registers.data?.find((r) => r.terminalId === selection.terminalId) ?? pickable[0] ?? null);

  /**
   * Write the selection to the URL. A register or a shift is a step of its
   * own in the history (Back returns to it); a date edit replaces the entry,
   * because a date typed digit by digit is several changes.
   */
  function select(
    change: Partial<{
      storeId: string | null;
      terminalId: string;
      fromDay: string;
      toDay: string;
    }> & {
      shiftId: string | undefined;
    },
    replace: boolean,
  ) {
    if (register === null) return;
    setParams(
      journalSearch({
        storeId: change.storeId !== undefined ? change.storeId : storeId,
        terminalId: change.terminalId ?? register.terminalId,
        fromDay: change.fromDay ?? selection.fromDay,
        toDay: change.toDay ?? selection.toDay,
        shiftId: change.shiftId,
      }),
      { replace },
    );
    // Picking the day the URL already has must clear a draft of that field too.
    setDraft({ search });
    setOpenId(null);
  }

  /**
   * A new store keeps the register and its shift when the register is in it;
   * otherwise the store's first till is shown, with no shift.
   */
  function changeStore(value: string) {
    if (register === null) return;
    const next = value === '' ? null : value;
    if (next === null || register.location?.id === next) {
      select({ storeId: next, shiftId }, false);
      return;
    }
    const first = journalPickableRegisters(registers.data ?? [], next)[0];
    if (first === undefined) return;
    select({ storeId: next, terminalId: first.terminalId, shiftId: undefined }, false);
  }

  function changeDay(key: 'from' | 'to', value: string) {
    if (isCalendarDay(value)) {
      select(
        key === 'from'
          ? { fromDay: value, shiftId: undefined }
          : { toDay: value, shiftId: undefined },
        true,
      );
    } else {
      setDraft({ ...liveDraft, [key]: value });
      setOpenId(null);
    }
  }

  // A URL this page cannot use (a register that is not offered, a malformed
  // day or shift) is corrected in place, so it is not copied on.
  const invalidKeys = selection.invalid.join(',');
  useEffect(() => {
    if (invalidKeys === '') return;
    const next = new URLSearchParams(search);
    for (const key of invalidKeys.split(',')) next.delete(key);
    setParams(next, { replace: true });
  }, [invalidKeys, search, setParams]);

  const days = daysBetween(fromDay, toDay);
  const rangeError =
    fromDay === '' || toDay === '' || days === 0
      ? t('fiscalCabinet.journal.rangeInvalid', 'The end date must not be before the start date.')
      : days > MAX_RANGE_DAYS
        ? t('fiscalCabinet.journal.rangeTooLong', 'Choose at most {days} days.').replace(
            '{days}',
            String(MAX_RANGE_DAYS),
          )
        : null;

  const scope = useMemo<JournalScope | null>(
    () =>
      register === null || rangeError !== null
        ? null
        : {
            terminalId: register.terminalId,
            from: dayStart(fromDay).toISOString(),
            to: nextDay(toDay).toISOString(),
            ...(shiftId !== undefined ? { shiftId } : {}),
          },
    [register, rangeError, fromDay, toDay, shiftId],
  );
  const summary = useJournalSummary(scope);
  const documents = useJournalDocuments(scope);
  const items: JournalEntry[] = documents.data?.pages.flatMap((p) => p.items) ?? [];
  // Cash carries no currency of its own: its shift's documents name it.
  const shiftCurrency = new Map(
    (summary.data?.shifts ?? []).map((s) => [
      s.shiftId,
      s.totals.mixedCurrency ? null : s.totals.currency,
    ]),
  );

  // A shift from the URL that is not on this register (the API answers 404):
  // show the whole range instead of an error.
  const shiftNotFound =
    shiftId !== undefined && (isNotFound(summary.error) || isNotFound(documents.error));
  useEffect(() => {
    if (!shiftNotFound) return;
    const next = new URLSearchParams(search);
    next.delete(JOURNAL_URL_KEYS.shift);
    setParams(next, { replace: true });
  }, [shiftNotFound, search, setParams]);

  async function runExport() {
    if (scope === null || register === null) return;
    setExporting(true);
    setExportError(false);
    try {
      await downloadJournalCsv(
        scope,
        `journal_${register.prro?.fiscalNumber ?? register.terminalCode}_${fromDay}_${toDay}.csv`,
      );
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  if (registers.isLoading || waitingForStore) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (registers.isError || registers.data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }
  if (registers.data.length === 0 || register === null) {
    return (
      <EmptyPanel>
        {t('fiscalCabinet.journal.noRegisters', 'There are no tills whose journal could be shown.')}
      </EmptyPanel>
    );
  }

  const stores = journalStores(registers.data);
  // A revoked register reached by a link stays selectable while it is shown.
  const options = pickable.some((r) => r.terminalId === register.terminalId)
    ? pickable
    : [register, ...pickable];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 space-y-1">
          <Label htmlFor="journal-store">{t('fiscalCabinet.journal.store', 'Store')}</Label>
          <Select
            id="journal-store"
            value={shownStore ?? ''}
            onChange={(e) => changeStore(e.target.value)}
          >
            <option value="">{t('fiscalCabinet.journal.allStores', 'All stores')}</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-72 flex-1 space-y-1">
          <Label htmlFor="journal-register">
            {t('fiscalCabinet.journal.register', 'PRRO / till')}
          </Label>
          <Select
            id="journal-register"
            value={register.terminalId}
            onChange={(e) => select({ terminalId: e.target.value, shiftId: undefined }, false)}
          >
            {options.map((r) => (
              <option key={r.terminalId} value={r.terminalId}>
                {r.terminalStatus === 'revoked'
                  ? t('fiscalCabinet.journal.registerRevoked', '{register} · revoked').replace(
                      '{register}',
                      () => registerLabel(r, t),
                    )
                  : registerLabel(r, t)}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="journal-from">{t('fiscalCabinet.journal.from', 'From')}</Label>
          <Input
            id="journal-from"
            type="date"
            value={fromDay}
            onChange={(e) => changeDay('from', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="journal-to">{t('fiscalCabinet.journal.to', 'To')}</Label>
          <Input
            id="journal-to"
            type="date"
            value={toDay}
            onChange={(e) => changeDay('to', e.target.value)}
          />
        </div>
        <Button variant="outline" disabled={scope === null || exporting} onClick={runExport}>
          {exporting
            ? t('fiscalCabinet.journal.exporting', 'Preparing…')
            : t('fiscalCabinet.journal.export', 'Export CSV')}
        </Button>
      </div>
      {rangeError !== null && <p className="text-sm text-destructive">{rangeError}</p>}
      {exportError && (
        <p className="text-sm text-destructive">
          {t('fiscalCabinet.journal.exportFailed', 'The export failed. Try a shorter period.')}
        </p>
      )}

      {summary.data !== undefined &&
        (summary.data.notUploaded.length > 0 ? (
          <p
            className="rounded-md border border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground"
            data-testid="journal-not-uploaded"
          >
            {t(
              'fiscalCabinet.journal.notUploaded',
              'Tills do not send the text of X and Z reports, cash in and cash out documents, the offline flag or the register’s own Z totals to Back Office yet. The totals below are computed from the receipts the tills uploaded.',
            )}
          </p>
        ) : (
          <p
            className="rounded-md border border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground"
            data-testid="journal-uploading"
          >
            {t(
              'fiscalCabinet.journal.uploading',
              'This till uploads the register’s X and Z reports, cash in and cash out documents and offline sessions: they are listed among the receipts as the register issued them. The totals below are computed from the receipts; a Z report’s own totals are in its document.',
            )}
          </p>
        ))}

      {summary.isLoading && scope !== null && (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      )}
      {summary.isError && (
        <p className="text-sm text-destructive">
          {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
        </p>
      )}
      {summary.data !== undefined && (
        <>
          <ShiftTotalsTable
            shifts={summary.data.shifts}
            totals={summary.data.totals}
            cash={summary.data.cash}
            selectedShiftId={shiftId}
            gapsTruncated={summary.data.numbering.gapsTruncated}
            onSelect={(next) => select({ shiftId: next }, false)}
            uploading={summary.data.fiscalJournal.uploading}
            onOpen={setOpenId}
          />
          <NumberingPanel summary={summary.data} onOpen={setOpenId} />
        </>
      )}

      {openId !== null && !items.some((d) => d.id === openId) && (
        <section className="rounded-md border border-border bg-card p-4">
          <EntryPanel id={openId} />
        </section>
      )}

      <section className="rounded-md border border-border bg-card">
        <header className="border-b border-border px-4 py-2 text-sm font-medium">
          {t('fiscalCabinet.journal.documents', 'Documents in fiscal order')}
        </header>
        {documents.isLoading && scope !== null ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        ) : documents.isError ? (
          <p className="px-4 py-3 text-sm text-destructive">
            {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('fiscalCabinet.journal.empty', 'No documents in this period.')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.fiscal.receiptNumber', 'Receipt number')}</TH>
                <TH>{t('receipts.field.kind', 'Kind')}</TH>
                <TH>{t('receipts.fiscal.fiscalNumber', 'Fiscal number')}</TH>
                <TH>{t('receipts.field.when', 'When')}</TH>
                <TH>{t('receipts.field.cashier', 'Cashier')}</TH>
                <TH className="text-right">{t('receipts.field.total', 'Total')}</TH>
                <TH>{t('fiscalCabinet.journal.col.payment', 'Payment')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((doc) =>
                doc.entry === 'fiscal_document' ? (
                  <Fragment key={doc.id}>
                    <FiscalDocumentRow
                      doc={doc}
                      currency={shiftCurrency.get(doc.shiftId) ?? null}
                      open={openId === doc.id}
                      onToggle={() => setOpenId(openId === doc.id ? null : doc.id)}
                    />
                    {openId === doc.id && (
                      <TR>
                        <TD colSpan={7} className="bg-muted/30">
                          <FiscalDocumentPanel documentId={doc.documentId} />
                        </TD>
                      </TR>
                    )}
                  </Fragment>
                ) : doc.entry !== 'document' ? (
                  <BackOfficeRecordRow
                    key={doc.id}
                    record={doc}
                    currency={shiftCurrency.get(doc.shiftId) ?? null}
                    uploading={summary.data?.fiscalJournal.uploading ?? false}
                  />
                ) : (
                  <Fragment key={doc.id}>
                    <TR
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        openId === doc.id && 'bg-muted',
                      )}
                      onClick={() => setOpenId(openId === doc.id ? null : doc.id)}
                      data-testid="journal-row"
                    >
                      <TD className="font-mono text-xs">{doc.receiptNumber ?? '—'}</TD>
                      <TD className="whitespace-nowrap">
                        {t(
                          JOURNAL_TYPE_LABEL_KEY[doc.receiptType],
                          JOURNAL_TYPE_LABEL[doc.receiptType],
                        )}
                        {!doc.hasFiscalBlock && doc.receiptType !== 'x_report' && (
                          <Badge variant="destructive" className="ml-2">
                            {t('fiscalCabinet.journal.noFiscalBlock', 'No fiscal block')}
                          </Badge>
                        )}
                        {doc.isLegacy && (
                          <Badge variant="outline" className="ml-2">
                            {t('receipts.list.legacyBadge', 'Legacy')}
                          </Badge>
                        )}
                      </TD>
                      <TD className="font-mono text-xs">{doc.fiscalNumber ?? '—'}</TD>
                      <TD className="whitespace-nowrap text-xs">{when(doc.finalisedAt)}</TD>
                      <TD>{doc.cashier?.displayName ?? '—'}</TD>
                      <TD className="text-right font-mono text-xs">
                        <Money minor={doc.totalMinor} currency={doc.currency} />
                      </TD>
                      <TD className="font-mono text-xs">
                        {paymentText(doc, t('fiscalCabinet.journal.discountLeg', 'discount'))}
                      </TD>
                    </TR>
                    {openId === doc.id && (
                      <TR>
                        <TD colSpan={7} className="bg-muted/30">
                          <DocumentPanel id={doc.id} />
                        </TD>
                      </TR>
                    )}
                  </Fragment>
                ),
              )}
            </TBody>
          </Table>
        )}
        {documents.hasNextPage && (
          <div className="border-t border-border px-4 py-2 text-right">
            <Button
              variant="outline"
              size="sm"
              disabled={documents.isFetchingNextPage}
              onClick={() => void documents.fetchNextPage()}
            >
              {t('fiscalCabinet.journal.more', 'Show more')}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

/** PFC-21 — Fiscal cabinet → Electronic journal. */
export function FiscalJournalPage() {
  return (
    <FiscalCabinetSection>{(available) => <Journal available={available} />}</FiscalCabinetSection>
  );
}
